import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, API, getToken } from "@/lib/api";
import { useAuth, canWrite, canManage } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import AssetImage from "@/components/AssetImage";
import AssetForm from "@/components/AssetForm";
import ImportAssetsDialog from "@/components/ImportAssetsDialog";
import BulkPhotosDialog from "@/components/BulkPhotosDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { MagnifyingGlass, Plus, DownloadSimple, UploadSimple, Images, Trash, PencilSimple, Spinner, CaretLeft, CaretRight, FunnelSimple, MapPin, Globe } from "@phosphor-icons/react";
import ImportFromSourceDialog from "@/components/ImportFromSourceDialog";
import { toast } from "sonner";

export default function Inventory() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 20 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [locFilter, setLocFilter] = useState(params.get("location_id") || "all");
  const [sortBy, setSortBy] = useState("updated_at");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => { const n = Number(localStorage.getItem("orbit_inventory_page_size")); return [25, 50, 75, 100].includes(n) ? n : 25; });
  const changePageSize = (n) => { setPageSize(n); setPage(1); localStorage.setItem("orbit_inventory_page_size", String(n)); };
  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [statuses, setStatuses] = useState(["Available", "In Service", "Sold", "Pending", "Missing"]);
  const [selected, setSelected] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [sourceOpen, setSourceOpen] = useState(false);
  const [photosOpen, setPhotosOpen] = useState(false);

  const writeAccess = canWrite(user.role);
  const manageAccess = canManage(user.role);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/assets", { params: { search, status, location_id: locFilter, sort_by: sortBy, page, page_size: pageSize } });
    setData(data);
    setLoading(false);
  }, [search, status, locFilter, sortBy, page, pageSize]);

  useEffect(() => {
    api.get("/locations").then(({ data }) => setLocations(data));
    api.get("/users").then(({ data }) => setEmployees(data)).catch(() => {});
    api.get("/company").then(({ data }) => setStatuses(data.statuses || statuses)).catch(() => {});
    // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (params.get("new") === "1" && writeAccess) {
      setEditing(null); setFormOpen(true);
      params.delete("new"); setParams(params, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  const toggleSel = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const allSelected = data.items.length > 0 && data.items.every((a) => selected.includes(a.id));

  const doDelete = async () => {
    await api.delete(`/assets/${deleteId}`);
    toast.success("Asset deleted");
    setDeleteId(null);
    load();
  };

  const bulkDelete = async () => {
    await api.post("/assets/bulk-delete", selected);
    toast.success(`${selected.length} assets deleted`);
    setSelected([]);
    load();
  };

  const bulkStatus = async (s) => {
    await api.post("/assets/bulk-status", { ids: selected, status: s });
    toast.success("Status updated");
    setSelected([]);
    load();
  };

  const downloadCsv = async () => {
    const res = await api.get("/reports/inventory", { params: { format: "csv" }, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = "inventory-report.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const totalPages = Math.ceil(data.total / pageSize) || 1;

  return (
    <div className="space-y-4" data-testid="inventory-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">Inventory</h1>
          <p className="text-zinc-500 text-sm mt-1">{data.total} total assets</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={downloadCsv} className="h-11 gap-2 border-zinc-300" data-testid="export-csv"><DownloadSimple size={18} weight="bold" /> <span className="hidden sm:inline">Export CSV</span></Button>
          {writeAccess && <Button variant="outline" onClick={() => setImportOpen(true)} className="h-11 gap-2 border-zinc-300" data-testid="import-csv"><UploadSimple size={18} weight="bold" /> <span className="hidden sm:inline">Import CSV</span></Button>}
          {writeAccess && <Button variant="outline" onClick={() => setSourceOpen(true)} className="h-11 gap-2 border-zinc-300" data-testid="import-source"><Globe size={18} weight="bold" /> <span className="hidden sm:inline">Import from Link / PDF</span></Button>}
          {writeAccess && <Button variant="outline" onClick={() => setPhotosOpen(true)} className="h-11 gap-2 border-zinc-300" data-testid="import-photos"><Images size={18} weight="bold" /> <span className="hidden sm:inline">Import Photos</span></Button>}
          {writeAccess && <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="h-11 gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold" data-testid="add-asset-button"><Plus size={18} weight="bold" /> Add Asset</Button>}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search VIN, stock #, make, model..." className="h-11 pl-10 focus:ring-2 focus:ring-emerald-500" data-testid="inventory-search" />
        </div>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <SelectTrigger className="h-11 w-40" data-testid="filter-status"><FunnelSimple size={16} className="mr-1" /><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={locFilter} onValueChange={(v) => { setLocFilter(v); setPage(1); }}>
          <SelectTrigger className="h-11 w-40" data-testid="filter-location"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Locations</SelectItem>
            {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}{typeof l.asset_count === "number" ? ` (${l.asset_count})` : ""}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="h-11 w-40" data-testid="sort-by"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="updated_at">Last Updated</SelectItem>
            <SelectItem value="created_at">Date Added</SelectItem>
            <SelectItem value="year">Year</SelectItem>
            <SelectItem value="make">Make</SelectItem>
            <SelectItem value="status">Status</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Bulk actions */}
      {selected.length > 0 && (
        <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="bulk-actions">
          <span className="text-sm font-semibold text-emerald-800">{selected.length} selected</span>
          {writeAccess && (
            <Select onValueChange={bulkStatus}>
              <SelectTrigger className="h-9 w-44 bg-white" data-testid="bulk-status"><SelectValue placeholder="Set status..." /></SelectTrigger>
              <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {manageAccess && <Button variant="destructive" size="sm" onClick={bulkDelete} className="h-9 gap-1.5" data-testid="bulk-delete"><Trash size={16} /> Delete</Button>}
          <Button variant="ghost" size="sm" onClick={() => setSelected([])} className="h-9 ml-auto">Clear</Button>
        </div>
      )}

      {/* Mobile cards */}
      <div className="sm:hidden space-y-2" data-testid="inventory-mobile-list">
        {loading ? <div className="py-16 text-center"><Spinner size={28} className="animate-spin text-emerald-600 mx-auto" /></div>
          : data.items.length === 0 ? <div className="rounded-lg border border-zinc-200 bg-white py-14 text-center text-zinc-400">No assets found.</div>
          : data.items.map((a) => (
            <div key={a.id} className="rounded-lg border border-zinc-200 bg-white p-3 flex gap-3 active:bg-zinc-50" onClick={() => navigate(`/app/assets/${a.id}`)} data-testid={`asset-card-${a.id}`}>
              <AssetImage path={a.photos?.[0]?.storage_path} className="h-16 w-20 object-cover rounded-md shrink-0" fallbackClassName="h-16 w-20 rounded-md shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-semibold text-sm truncate">{a.title || a.asset_id}</p>
                  <StatusBadge status={a.status} />
                </div>
                <p className="text-xs text-zinc-500 mt-0.5">{a.asset_id}{a.stock_number ? ` · Stock ${a.stock_number}` : ""}</p>
                <p className="text-xs text-zinc-500 truncate">{[a.location_name, a.details?.job_site, a.assigned_employee_name].filter(Boolean).join(" · ") || "—"}</p>
                {a.identifier && <p className="text-[11px] font-mono text-zinc-400 truncate">{a.identifier}</p>}
              </div>
              <div onClick={(e) => e.stopPropagation()} className="flex items-center"><Checkbox checked={selected.includes(a.id)} onCheckedChange={() => toggleSel(a.id)} className="h-5 w-5" data-testid={`select-card-${a.id}`} /></div>
            </div>))}
      </div>

      {/* Table */}
      <div className="hidden sm:block rounded-lg border border-zinc-200 bg-white overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50">
              <TableHead className="w-10"><Checkbox checked={allSelected} onCheckedChange={() => setSelected(allSelected ? [] : data.items.map((a) => a.id))} data-testid="select-all" /></TableHead>
              <TableHead>Photo</TableHead>
              <TableHead>Asset ID</TableHead>
              <TableHead>Stock #</TableHead>
              <TableHead>Asset</TableHead>
              <TableHead>VIN / Serial</TableHead>
              <TableHead>Mileage</TableHead>
              <TableHead>Location / Job Site</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Assigned</TableHead>
              <TableHead>Updated</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={12} className="text-center py-16"><Spinner size={28} className="animate-spin text-emerald-600 mx-auto" /></TableCell></TableRow>
            ) : data.items.length === 0 ? (
              <TableRow><TableCell colSpan={12} className="text-center py-16 text-zinc-400">No assets found. {writeAccess && "Click 'Add Asset' to create your first one."}</TableCell></TableRow>
            ) : data.items.map((a) => (
              <TableRow key={a.id} className="cursor-pointer hover:bg-zinc-50" data-testid={`asset-row-${a.id}`}>
                <TableCell onClick={(e) => e.stopPropagation()}><Checkbox checked={selected.includes(a.id)} onCheckedChange={() => toggleSel(a.id)} data-testid={`select-${a.id}`} /></TableCell>
                <TableCell onClick={() => navigate(`/app/assets/${a.id}`)}>
                  <AssetImage path={a.photos?.[0]?.storage_path} className="h-10 w-14 object-cover rounded-md" fallbackClassName="h-10 w-14 rounded-md" />
                </TableCell>
                <TableCell onClick={() => navigate(`/app/assets/${a.id}`)} className="font-semibold text-sm">{a.asset_id}</TableCell>
                <TableCell onClick={() => navigate(`/app/assets/${a.id}`)} className="text-sm">{a.stock_number || "—"}</TableCell>
                <TableCell onClick={() => navigate(`/app/assets/${a.id}`)} className="text-sm font-medium">
                  <div>{a.title || "—"}</div>
                  <div className="text-xs text-zinc-400">{a.type_label}</div>
                </TableCell>
                <TableCell onClick={() => navigate(`/app/assets/${a.id}`)} className="text-xs font-mono text-zinc-500">{a.identifier || "—"}</TableCell>
                <TableCell onClick={() => navigate(`/app/assets/${a.id}`)} className="text-sm">{a.mileage || "—"}</TableCell>
                <TableCell onClick={() => navigate(`/app/assets/${a.id}`)} className="text-sm">
                  <div>{a.location_name || "—"}</div>
                  {a.details?.job_site && (() => {
                    const ret = a.details.job_site_return;
                    const overdue = ret && new Date(ret) < new Date(new Date().toDateString());
                    const fmt = (d) => d ? new Date(d + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";
                    return (
                      <div className="mt-1" data-testid={`jobsite-${a.id}`}>
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700"><MapPin size={12} weight="fill" /> {a.details.job_site}</span>
                        {(a.details.job_site_date || ret) && (
                          <div className={`text-xs ${overdue ? "text-red-600 font-semibold" : "text-zinc-400"}`}>
                            {a.details.job_site_date && `On ${fmt(a.details.job_site_date)}`}
                            {a.details.job_site_date && ret && " · "}
                            {ret && `Return ${fmt(ret)}`}
                            {overdue && " (overdue)"}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </TableCell>
                <TableCell onClick={() => navigate(`/app/assets/${a.id}`)}><StatusBadge status={a.status} /></TableCell>
                <TableCell onClick={() => navigate(`/app/assets/${a.id}`)} className="text-sm">{a.assigned_employee_name || "—"}</TableCell>
                <TableCell onClick={() => navigate(`/app/assets/${a.id}`)} className="text-xs text-zinc-500">{new Date(a.updated_at).toLocaleDateString()}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <div className="flex gap-1">
                    {writeAccess && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditing(a); setFormOpen(true); }} data-testid={`edit-${a.id}`}><PencilSimple size={16} /></Button>}
                    {manageAccess && <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => setDeleteId(a.id)} data-testid={`delete-${a.id}`}><Trash size={16} /></Button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="inventory-pagination">
        <p className="text-sm text-zinc-500" data-testid="pagination-summary">
          {data.total === 0 ? "No assets" : `Showing ${(data.page - 1) * pageSize + 1}–${Math.min(data.page * pageSize, data.total)} of ${data.total.toLocaleString()}`} · Page {data.page} of {totalPages}
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1 text-sm text-zinc-500" data-testid="page-size-picker">
            <span className="mr-1">Show</span>
            {[25, 50, 75, 100].map((n) => (
              <button key={n} type="button" onClick={() => changePageSize(n)} className={`h-8 min-w-[2.25rem] px-2 rounded-md text-sm font-semibold transition-colors ${pageSize === n ? "bg-zinc-900 text-white" : "border border-zinc-300 text-zinc-700 hover:bg-zinc-100"}`} aria-pressed={pageSize === n} data-testid={`page-size-${n}`}>{n}</button>
            ))}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="h-9 border-zinc-300" data-testid="prev-page"><CaretLeft size={16} /></Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="h-9 border-zinc-300" data-testid="next-page"><CaretRight size={16} /></Button>
          </div>
        </div>
      </div>

      <AssetForm open={formOpen} onOpenChange={setFormOpen} asset={editing} locations={locations} employees={employees} statuses={statuses} onSaved={load} />

      <ImportAssetsDialog open={importOpen} onOpenChange={setImportOpen} onImported={() => { setPage(1); load(); }} />
      <ImportFromSourceDialog open={sourceOpen} onOpenChange={setSourceOpen} onImported={() => { setPage(1); load(); }} />

      <BulkPhotosDialog open={photosOpen} onOpenChange={setPhotosOpen} onUploaded={load} />

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete asset?</AlertDialogTitle><AlertDialogDescription>This permanently removes the asset. Movement history is retained in the ledger.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
